import type { SignupSubRouteRouteContext } from 'router/utils';

import { clearSessionStorage } from 'config/storage';
import { resolveEventData } from 'services/eventTracker/utils';
import {
  completeLead,
  readLeadQueryCache,
} from 'services/partnersSelfSignup/signup';
import { rumService } from 'services/rum';
import { trackConversionEvents } from 'utils/experimentation';

type LoaderProps = {
  context: SignupSubRouteRouteContext;
  preload: boolean;
};

export async function successLoader({ context, preload }: LoaderProps) {
  const { queryClient, eventTracking, rum } = context;

  const variables = context.queryVariables;
  const lead = readLeadQueryCache(context.queryClient, variables);

  if (lead?.lead && variables.leadId && !preload) {
    try {
      const mutationVariables = {
        ...variables,
        step: lead?.step,
      };

      const mutationKey = ['complete', mutationVariables];
      const mutationCache = queryClient.getMutationCache();
      const completeMutation = mutationCache.find({
        mutationKey,
      });

      if (completeMutation) {
        if (completeMutation.state.status !== 'error') {
          return;
        }

        // remove the mutation from the cache on error
        mutationCache.remove(completeMutation);
      }

      const mutation = mutationCache.build(queryClient, {
        mutationKey,
        mutationFn: completeLead,
      });
      await mutation.execute(mutationVariables);

      trackConversionEvents({
        eventName: 'partner_self_sign_up_completed',
        contextData: context.growthbook.getAttributes(),
        snowplowRecord: context.eventTracking.record,
      });

      rumService.addAction('successful_signup_action');

      clearSessionStorage(queryClient);
    } catch (error) {
      const eventData = resolveEventData(
        {
          category: 'success',
          label: 'failed_to_complete_signup',
        },
        lead.lead,
      );

      eventTracking.recordErrorView(eventData);
      rum?.addError(
        new Error(`Failed to complete signup by completeLead method`, {
          cause: error,
        }),
        eventData,
      );
    }
  }
}
